import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Chip,
  CircularProgress,
  TextField,
  InputAdornment
} from '@mui/material';
import {
  GetApp as DownloadIcon,
  InsertDriveFile as FileIcon,
  PictureAsPdf as PdfIcon,
  Description as DocIcon,
  GridOn as SheetIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useProfile } from '../../contexts/ProfileContext';
import {
  getSharedWithMe,
  formatFileSize,
  getFileIcon
} from '../../services/familyDocumentService';
import logger from '../../utils/logger';
import { openSafeUrl } from '../../utils/urlSanitizer';

const SharedWithMeTab = () => {
  const { currentUser } = useAuth();
  const { profile } = useProfile();
  const [documents, setDocuments] = useState([]);
  const [filteredDocuments, setFilteredDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  // Load shared documents
  useEffect(() => {
    const loadSharedDocuments = async () => {
      if (currentUser && profile?.familyCode) {
        try {
          setLoading(true);
          const docs = await getSharedWithMe(currentUser.uid, profile.familyCode);
          setDocuments(docs);
          setFilteredDocuments(docs);
        } catch (error) {
          logger.error('Error loading shared documents:', error);
        } finally {
          setLoading(false);
        }
      }
    };
    loadSharedDocuments();
  }, [currentUser, profile]);

  // Filter documents based on search
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredDocuments(documents);
    } else {
      const filtered = documents.filter((doc) =>
        doc.fileName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        doc.uploadedByName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDocuments(filtered);
    }
  }, [searchTerm, documents]);

  const getFileIconComponent = (fileType) => {
    const iconType = getFileIcon(fileType);
    switch (iconType) {
      case 'pdf':
        return <PdfIcon color="error" />;
      case 'document':
        return <DocIcon color="primary" />;
      case 'spreadsheet':
        return <SheetIcon color="success" />;
      case 'image':
        return <FileIcon color="info" />;
      default:
        return <FileIcon />;
    }
  };

  const getSourceChip = (source) => {
    if (source === 'Family Vault') {
      return <Chip label="Family Vault" size="small" color="warning" />;
    }
    return <Chip label="Personal Documents" size="small" color="primary" />;
  };

  const getPermissionInfo = (doc) => {
    if (doc.sharedType === 'vault' && doc.accessGrantedTo) {
      const myAccess = doc.accessGrantedTo.find((access) => access.userId === currentUser.uid);
      if (myAccess) {
        return myAccess.permissions === 'view' ? 'View Only' : 'Can Download';
      }
    }
    return 'View';
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6">Shared With Me</Typography>
        <Typography variant="body2" color="text.secondary">
          Files that family members have shared with you
        </Typography>
      </Box>

      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search shared files..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            )
          }}
        />
      </Box>

      {filteredDocuments.length === 0 ? (
        <Alert severity="info">
          {searchTerm
            ? 'No shared documents match your search.'
            : 'No shared documents yet. Family members can share documents with you from the vault.'}
        </Alert>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>File Name</TableCell>
                <TableCell>Source</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>Shared By</TableCell>
                <TableCell>Permission</TableCell>
                <TableCell>Shared Date</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredDocuments.map((doc) => (
                <TableRow key={doc.id}>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {getFileIconComponent(doc.fileType)}
                      <Typography>{doc.fileName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>
                    {getSourceChip(doc.source)}
                  </TableCell>
                  <TableCell>{formatFileSize(doc.fileSize)}</TableCell>
                  <TableCell>{doc.uploadedByName || 'Unknown'}</TableCell>
                  <TableCell>
                    <Chip
                      label={getPermissionInfo(doc)}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {doc.uploadedAt?.toDate?.()?.toLocaleDateString() || 'Unknown'}
                  </TableCell>
                  <TableCell align="right">
                    <IconButton
                      size="small"
                      onClick={() => openSafeUrl(doc.downloadURL)}
                      title="Download"
                    >
                      <DownloadIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {documents.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="caption" color="text.secondary">
            Showing {filteredDocuments.length} of {documents.length} shared document(s)
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default SharedWithMeTab;
